import { toNumber, fullName, yearOf } from './assessmentSelectors.js';

// The cash flow, drawn: the timeline that sits under the whiteboard.
//
// This is the "Illustrating Cash Flow" training video, made deterministic. The
// advisor lead draws a timeline left to right: today and what you spend;
// retirement and what you will need; the years before Social Security, with
// nothing coming in; Social Security starting and the shortfall shrinking; and
// whether what is left lasts to 95. The beats he lands are the cumulative draw
// in the gap years as a share of the portfolio, and the distribution rate in
// each stretch against a 3 to 4% target. Every one of those is arithmetic on
// the timeline assessmentSelectors already builds, so the board can be ruled
// with the household's own numbers without a single new assumption, and the
// advisor draws the story over the ruling.
//
// Two conventions carried on purpose. NO GROWTH: the pool walks down by the
// shortfall and nothing else, which understates a real portfolio and is
// therefore checkable by hand. And every need is BEFORE TAX AND COLA, the goal
// sheet's own "+Tax+COLA", because the questionnaire records what they spend,
// not what they must withdraw to spend it. The board says so in its header.
//
// Three things the ruling refuses to do, because each would mislead:
//   - pass one year's figure off as a whole stretch's when the years differ,
//     so a stretch states its range when it has one;
//   - print a withdrawal rate on savings that have already run out, so a
//     stretch that empties the pool says so, and names the year;
//   - drop a moment it had no room to label, so the moments not ticked are
//     listed on the board.
//
// The result is drawn, and its figures are saved WITH the advisor's strokes,
// never instead of them. A board reopened after the questionnaire changed is
// ruled as it was when the ink went on, and says so.

// Today and the horizon always draw. The moments between them compete for
// four slots, because a board with nine labelled moments is not a board anyone
// can draw on. Retirement and Social Security outrank a pension, which
// outranks a spending change; ties go to whichever moves the shortfall more.
const MAX_MIDDLE_TICKS = 4;
const PRIORITY = { retire: 1, ss: 1, pension: 2, spending: 3 };

export function getCashFlowTimeline(data, context = {}) {
  const { timeline, taxPools, assumptions = {} } = context;
  const rows = Array.isArray(timeline) ? timeline.filter(Boolean) : [];
  if (!rows.length || !taxPools) return null;

  const family = data?.family || {};
  const income = data?.income || {};
  const te = data?.taxesExpenses || {};
  const currentYear = rows[0].year;
  const lastYear = rows[rows.length - 1].year;

  const c1 = {
    key: 'client1',
    name: (family.client1FirstName || '').trim() || 'You',
    ageNow: toNumber(family.client1Age),
  };
  const c2Full = fullName(family.client2FirstName, family.client2LastName);
  const c2 = c2Full && toNumber(family.client2Age) > 0
    ? { key: 'client2', name: (family.client2FirstName || '').trim(), ageNow: toNumber(family.client2Age) }
    : null;
  const people = [c1, c2].filter(Boolean);
  // Same owner rule as buildIncomeTimeline: anyone who is not client 2 is
  // client 1, because a pension owner is often the payer rather than a person.
  const ownerOf = row => (c2 && row?.owner === c2Full ? c2 : c1);
  const yearWhen = (person, age) => currentYear + (age - person.ageNow);
  const rowsOf = key => (Array.isArray(income[key]) ? income[key].filter(Boolean) : []);
  const gapIn = year => {
    const r = rows.find(x => x.year === year);
    return r ? r.gap : 0;
  };

  const candidates = [];
  const add = (year, kind, label) => {
    // A moment already behind them, or past the horizon, is not a tick.
    if (!(year > currentYear) || year > lastYear) return;
    candidates.push({ year, kind, label });
  };

  people.forEach(p => {
    const retireAge = toNumber(p.key === 'client1' ? assumptions.c1RetireAge : assumptions.c2RetireAge);
    const working = rowsOf('employment').some(r => ownerOf(r) === p);
    if (working && retireAge > p.ageNow) add(yearWhen(p, retireAge), 'retire', `${p.name} retires`);
  });
  rowsOf('socialSecurity').forEach(r => {
    const p = ownerOf(r);
    const startAge = toNumber(r.startingAge);
    if (startAge > 0) add(yearWhen(p, startAge), 'ss', `${p.name}'s Social Security starts`);
  });
  rowsOf('pension').forEach(r => {
    const p = ownerOf(r);
    const startAge = toNumber(r.startAge);
    const year = startAge > 0 ? yearWhen(p, startAge) : yearOf(r.startDate);
    if (year) add(year, 'pension', people.length > 1 ? `${p.name}'s pension starts` : 'Pension starts');
  });
  const expenses = Array.isArray(te.expenses) ? te.expenses.filter(Boolean) : [];
  expenses.forEach(r => {
    const desc = String(r.description || '').trim();
    const startYear = yearOf(r.startDate);
    const endYear = yearOf(r.endDate);
    if (startYear) add(startYear, 'spending', desc ? `${desc} begins` : 'Spending changes');
    // The first year WITHOUT the expense is the moment the picture changes.
    if (endYear) add(endYear + 1, 'spending', desc ? `${desc} ends` : 'Spending changes');
  });

  const byYear = new Map();
  candidates.forEach(c => {
    const cur = byYear.get(c.year);
    if (cur) {
      cur.labels.push(c.label);
      cur.priority = Math.min(cur.priority, PRIORITY[c.kind]);
    } else {
      byYear.set(c.year, {
        year: c.year,
        kind: c.kind,
        labels: [c.label],
        priority: PRIORITY[c.kind],
        swing: Math.abs(gapIn(c.year) - gapIn(c.year - 1)),
      });
    }
  });
  const middle = [...byYear.values()]
    .sort((a, b) => a.priority - b.priority || b.swing - a.swing || a.year - b.year)
    .slice(0, MAX_MIDDLE_TICKS)
    .sort((a, b) => a.year - b.year);
  const ticked = new Set(middle.map(t => t.year));
  // What the ticks had no room for is still true, and is listed on the board.
  const hidden = [...byYear.values()]
    .filter(t => !ticked.has(t.year))
    .sort((a, b) => a.year - b.year)
    .map(t => ({ year: t.year, label: t.labels.join(' · ') }));

  const agesIn = year => people.map(p => ({ name: p.name, age: p.ageNow + (year - currentYear) }));
  // The timeline ends when the younger of the two reaches the horizon age, so
  // the last tick names that person; the other's age travels with it.
  const youngest = people.reduce((a, b) => (b.ageNow < a.ageNow ? b : a), people[0]);
  const ticks = [
    { year: currentYear, kind: 'today', label: 'Today', ages: agesIn(currentYear) },
    ...middle.map(t => ({ year: t.year, kind: t.kind, label: t.labels.join(' · '), ages: agesIn(t.year) })),
    {
      year: lastYear,
      kind: 'horizon',
      label: `${youngest.name} reaches ${youngest.ageNow + (lastYear - currentYear)}`,
      ages: agesIn(lastYear),
    },
  ];

  // Segments between ticks. The headline figure is the first year's; the
  // range across the stretch is carried beside it so the ruling can say when
  // the years differ. The cumulative is the exact sum, so a small change the
  // ticks had no room to label still counts in the total. The pool walks down
  // year by year, so the year it runs out is known exactly.
  const liquid = toNumber(taxPools.total);
  let pool = liquid;
  let cumulativeShortfall = 0;
  let shortfallYears = 0;
  let depletionYear = null;
  const segments = [];
  for (let i = 0; i < ticks.length - 1; i += 1) {
    const from = ticks[i].year;
    const last = i === ticks.length - 2;
    const to = last ? ticks[i + 1].year : ticks[i + 1].year - 1;
    const span = rows.filter(r => r.year >= from && r.year <= to);
    if (!span.length) continue;
    const first = span[0];
    const gaps = span.map(r => Math.max(0, r.gap));
    const shortfall = gaps[0];
    const cumulative = gaps.reduce((s, g) => s + g, 0);
    const shortfallMin = Math.min(...gaps);
    const shortfallMax = Math.max(...gaps);
    const poolBefore = pool;
    let depletedHere = null;
    span.forEach((r, j) => {
      pool -= gaps[j];
      if (pool < 0 && depletionYear === null) {
        depletionYear = r.year;
        depletedHere = r.year;
      }
    });
    // A stretch that empties the pool names the year; a later stretch with
    // nothing left to draw on names the year it already ran out.
    const depleted = depletedHere !== null || (poolBefore <= 0 && shortfall > 0);
    segments.push({
      fromYear: from,
      toYear: to,
      years: span.length,
      need: first.spendingNeed,
      income: first.totalIncome,
      shortfall,
      shortfallMin,
      shortfallMax,
      varies: shortfallMax - shortfallMin > 0,
      surplus: Math.max(0, -first.gap),
      cumulative,
      poolBefore,
      // Zero growth: what a year of this stretch takes out of what was left
      // when the stretch began. The video's "that's almost a quarter".
      rate: poolBefore > 0 && shortfall > 0 ? shortfall / poolBefore : 0,
      shareOfLiquid: liquid > 0 ? cumulative / liquid : 0,
      depleted,
      depletedYear: depleted ? depletedHere ?? depletionYear : null,
    });
    cumulativeShortfall += cumulative;
    shortfallYears += span.filter(r => r.gap > 0).length;
  }

  const firstShort = rows.find(r => r.gap > 0);
  return {
    years: { first: currentYear, last: lastYear },
    liquid,
    people,
    ticks,
    hidden,
    segments,
    totals: {
      cumulativeShortfall,
      shortfallYears,
      shareOfLiquid: liquid > 0 ? cumulativeShortfall / liquid : 0,
      firstShortfallYear: firstShort ? firstShort.year : null,
      remaining: liquid - cumulativeShortfall,
      lastsToHorizon: liquid - cumulativeShortfall >= 0,
      depletionYear,
    },
    targetRate: { low: 0.03, high: 0.04 },
    // The five questions the video has the advisor write on the board, in his
    // order. Worded as questions because nothing here is a recommendation.
    decisions: [
      'When can you retire?',
      'What is a safe amount to spend each year?',
      'What balance do you need at retirement, and which accounts do you draw from first?',
      'When do you start Social Security? Anywhere from 62 to 70.',
      'What about taxes? Every figure here is before tax.',
    ],
  };
}
